/**
 * Tagebuch-Sperre (E14): Face ID vor dem Tagebuch, je Gerät ein- und
 * abschaltbar.
 *
 * Das ist eine Sichtsperre, keine Verschlüsselung: Die Daten liegen weiter im
 * Gerätespeicher und auf dem Server. Sie hilft gegen den, der das entsperrte
 * Telefon kurz in der Hand hat — nicht gegen den, der das Gerät ausliest.
 *
 * Entsperrt wird mit dem Passkey des Kontos, **lokal**: Challenge vom Gerät,
 * keine Prüfung beim Server. So geht es auch offline, und mehr als „das
 * Betriebssystem hat Face ID verlangt und den Schlüssel dieses Kontos benutzt"
 * braucht eine Sichtsperre nicht. Geprüft wird beides: das UV-Bit in den
 * Authenticator-Daten und dass der Schlüssel dem angemeldeten Konto gehört
 * (`userHandle`). Ausweg ohne Face ID: das Passwort, über den Server.
 *
 * Entsperrt bleibt das Tagebuch, bis die App in den Hintergrund geht; wer es
 * nach mehr als fünf Minuten wieder öffnet, wird erneut gefragt. Gesperrt wird
 * über die Anzeige gelegt, nicht abgebaut — ein halb geschriebener Gedanke
 * überlebt das.
 */

import { startAuthentication } from '@simplewebauthn/browser';
import type { AuthenticationResponseJSON } from '@simplewebauthn/browser';
import { apiFetch } from './api.js';
import { currentOwnerId } from './auth.js';
import { db } from './db/local.js';
import type { KraftwerkDatabase } from './db/local.js';

const LOCK_KEY = 'journalLock';
export const UNLOCK_GRACE_MS = 5 * 60 * 1000;

export async function readJournalLock(database: KraftwerkDatabase = db): Promise<boolean> {
  return (await database.meta.get(LOCK_KEY))?.value === 'an';
}

export async function writeJournalLock(
  on: boolean,
  database: KraftwerkDatabase = db,
): Promise<void> {
  await database.meta.put({ key: LOCK_KEY, value: on ? 'an' : 'aus' });
}

// --- Zustand im Arbeitsspeicher ----------------------------------------------

let unlockedAt: number | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

export function subscribeUnlock(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Zeitpunkt des letzten Entsperrens — für `useSyncExternalStore`. */
export function unlockedSnapshot(): number | null {
  return unlockedAt;
}

/** Noch entsperrt? Gilt nach dem Entsperren fünf Minuten fürs erneute Öffnen. */
export function isUnlocked(now: number = Date.now()): boolean {
  return unlockedAt !== null && now - unlockedAt < UNLOCK_GRACE_MS;
}

export function markUnlocked(now: number = Date.now()): void {
  unlockedAt = now;
  notify();
}

export function lockJournal(): void {
  if (unlockedAt === null) return;
  unlockedAt = null;
  notify();
}

/** In den Hintergrund → sperren. Einmal beim Start eingehängt. */
export function watchBackground(target: Document = document): () => void {
  const onChange = () => {
    if (target.visibilityState === 'hidden') lockJournal();
  };
  target.addEventListener('visibilitychange', onChange);
  return () => target.removeEventListener('visibilitychange', onChange);
}

// --- Entsperren ---------------------------------------------------------------

export type Authenticate = (options: {
  challenge: string;
  rpId: string;
}) => Promise<AuthenticationResponseJSON>;

const defaultAuthenticate: Authenticate = ({ challenge, rpId }) =>
  startAuthentication({
    optionsJSON: { challenge, rpId, userVerification: 'required', allowCredentials: [] },
  });

function base64url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64url(value: string): Uint8Array {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

/** Bit 2 der Flags (Byte 32 der Authenticator-Daten): Nutzer verifiziert. */
const USER_VERIFIED = 0x04;

export type UnlockResult = 'unlocked' | 'cancelled' | 'notVerified' | 'otherAccount';

export async function unlockWithFaceId(
  authenticate: Authenticate = defaultAuthenticate,
  ownerId: string | null = currentOwnerId(),
): Promise<UnlockResult> {
  const challenge = base64url(crypto.getRandomValues(new Uint8Array(32)));
  let response: AuthenticationResponseJSON;
  try {
    response = await authenticate({ challenge, rpId: location.hostname });
  } catch {
    return 'cancelled';
  }
  const data = fromBase64url(response.response.authenticatorData);
  if (((data[32] ?? 0) & USER_VERIFIED) === 0) return 'notVerified';
  const handle = response.response.userHandle;
  const owner = handle ? new TextDecoder().decode(fromBase64url(handle)) : null;
  if (!ownerId || owner !== ownerId) return 'otherAccount';
  markUnlocked();
  return 'unlocked';
}

/**
 * Sperre an- oder abschalten — beides nur mit Face ID (Ausschalten innerhalb
 * der Frist nach dem Entsperren ohne erneute Frage).
 *
 * **Anschalten sperrt sofort** (23.10.2026, am iPhone gemeldet): Das Face ID
 * zum Bestätigen zählte vorher als Entsperren, und das Tagebuch blieb offen,
 * bis die App in den Hintergrund ging. Wer die Sperre anschaltet, will sie
 * jetzt sehen.
 */
export async function switchJournalLock(
  next: boolean,
  authenticate: Authenticate = defaultAuthenticate,
  database: KraftwerkDatabase = db,
  ownerId: string | null = currentOwnerId(),
): Promise<UnlockResult> {
  if (!next && isUnlocked()) {
    await writeJournalLock(false, database);
    return 'unlocked';
  }
  const outcome = await unlockWithFaceId(authenticate, ownerId);
  if (outcome !== 'unlocked') return outcome;
  await writeJournalLock(next, database);
  if (next) lockJournal();
  return outcome;
}

/** Ausweg: das Passwort, geprüft beim Server (nur online). */
export async function unlockWithPassword(password: string): Promise<void> {
  await apiFetch<undefined>('/passkeys/verify-password', {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
  markUnlocked();
}
